// File Metadata:
// Filename: receptionsApiSlice.js
// Version: 1.00
// Created: 2022-09-28 22:18:59
// Last Modified: 2025-04-01 16:55:19
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { createSelector, createEntityAdapter } from '@reduxjs/toolkit'
import { apiSlice } from '../../app/api/apiSlice'

const receptionsAdapter = createEntityAdapter({
	sortComparer: (a, b) => (a.completed === b.completed ? 0 : a.completed ? 1 : -1)
})

const initialState = receptionsAdapter.getInitialState()

export const receptionsApiSlice = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getReceptions: builder.query({
			query: () => ({
				url: '/receptions',
				validateStatus: (response, result) => {
					return response.status === 200 && !result.isError
				}
			}),
			transformResponse: responseData => {
				//console.log('responseData: ' + JSON.stringify(responseData))
				const loadedReceptions = responseData.map(reception => {
					reception.id = reception._id
					return reception
				})
				//console.log('loadedReceptions: ' + JSON.stringify(loadedReceptions))
				//console.log(initialState)
				//console.log(loadedReceptions)
				return receptionsAdapter.setAll(initialState, loadedReceptions)
			},
			providesTags: (result, error, arg) => {
				console.log(result)
				if (result?.ids) {
					return [{ type: 'Reception', id: 'LIST' }, ...result.ids.map(id => ({ type: 'Reception', id }))] 
				} else return [{ type: 'Reception', id: 'LIST' }]
			}
		}),
		addNewReception: builder.mutation({
			query: initialReception => ({
				url: '/receptions',
				method: 'POST',
				body: { 
					...initialReception,
					qualityStatus: 'PENDING_APPROVAL' // Set initial status
				}
			}),
			invalidatesTags: (result, error, arg) => [{ type: 'Reception', id: 'LIST' }]
		}),
		updateReception: builder.mutation({
			query: initialReception => ({
				url: '/receptions',
				method: 'PATCH',
				body: { ...initialReception }
			}),
			invalidatesTags: (result, error, arg) => [{ type: 'Reception', id: arg.id }]
		}),
		deleteReception: builder.mutation({
			query: ({ id }) => ({
				url: '/receptions',
				method: 'DELETE',
				body: { id }
			}),
			invalidatesTags: (result, error, arg) => [{ type: 'Reception', id: arg.id }]
		}),
		updateQualityStatus: builder.mutation({
			query: ({ id, status, notes }) => ({
				url: `/receptions/${id}/quality`,
				method: 'PATCH',
				body: { status, notes }
			}),
			invalidatesTags: (result, error, { id }) => [
				{ type: 'Reception', id }
			]
		}),
		addQualityCheck: builder.mutation({
			query: ({ id, check }) => ({
				url: `/receptions/${id}/quality/checks`,
				method: 'POST',
				body: check
			}),
			invalidatesTags: (result, error, arg) => [
				{ type: 'Reception', id: arg.id }
			]
		}),
		addQualityNote: builder.mutation({
			query: ({ id, note }) => ({
				url: `/receptions/${id}/quality/notes`,
				method: 'POST',
				body: { ...note }  // Spread the note object to send its properties directly
			}),
			// Update cache to include new note
			async onQueryStarted({ id, note }, { dispatch, queryFulfilled }) {
				try {
					const { data: newNote } = await queryFulfilled
					dispatch(
						receptionsApiSlice.util.updateQueryData('getReceptions', 'receptionsList', draft => {
							const reception = draft.entities[id]
							if (reception) {
								reception.qualityNotes = reception.qualityNotes || []
								reception.qualityNotes.push(newNote)
							}
						})
					)
				} catch {}
			},
			invalidatesTags: (result, error, arg) => [
				{ type: 'Reception', id: arg.id }
			]
		}),
		addQualityPhoto: builder.mutation({
			query: ({ id, photo }) => ({
				url: `/receptions/${id}/quality/photos`,
				method: 'POST',
				body: { ...photo }
			}),
			async onQueryStarted({ id, photo }, { dispatch, queryFulfilled }) {
				try {
					const { data: newPhoto } = await queryFulfilled
					dispatch(
						receptionsApiSlice.util.updateQueryData('getReceptions', 'receptionsList', draft => {
							const reception = draft.entities[id]
							if (reception) {
								reception.qualityPhotos = reception.qualityPhotos || []
								reception.qualityPhotos.push(newPhoto)
							}
						})
					)
				} catch {}
			},
			invalidatesTags: (result, error, arg) => [
				{ type: 'Reception', id: arg.id }
			]
		})
	})
})

export const {
	useGetReceptionsQuery,
	useAddNewReceptionMutation,
	useUpdateReceptionMutation,
	useDeleteReceptionMutation,
	useUpdateQualityStatusMutation,
	useAddQualityCheckMutation,
	useAddQualityNoteMutation,
	useAddQualityPhotoMutation
} = receptionsApiSlice

export const selectReceptionsResult = receptionsApiSlice.endpoints.getReceptions.select()

const selectReceptionsData = createSelector(selectReceptionsResult, receptionsResult => receptionsResult.data)

export const {
	selectAll: selectAllReceptions,
	selectById: selectReceptionById,
	selectIds: selectReceptionIds
} = receptionsAdapter.getSelectors(state => selectReceptionsData(state) ?? initialState)
